/**
 * ⭐ Maintenance energy estimate — the 2023 Dietary Reference Intakes for Energy,
 * adult Estimated Energy Requirement (EER) equations (owner-approved, 2026-10-09).
 *
 * SOURCE: National Academies of Sciences, Engineering, and Medicine. 2023. *Dietary
 * Reference Intakes for Energy.* Washington, DC: The National Academies Press.
 * doi:10.17226/26818 — Summary, Table S-3 (= Table S-1), adults 19+:
 *   EER (kcal/d) = A − (B × age y) + (C × height cm) + (D × weight kg)
 * Activity categories: Table 5-4 (adults: inactive 1.00 ≤ PAL < 1.53 · low active
 * 1.53–1.68 · active 1.68–1.85 · very active 1.85–2.50); plain-language examples: Table 7-1.
 * Typical error (RMSE): men 339, women 246 kcal/d (Table S-1 notes).
 *
 * ⚠️⚠️ VERIFIED, NOT ASSUMED: the coefficients were read from the raw page text (S-1 and
 * S-3 agree), and `energyEstimate.test.ts` reproduces all 40 EERs the report publishes
 * for median U.S. adults (Tables 7-9 / 7-10) within 1 kcal. Change a coefficient and
 * those tests fail.
 *
 * SCOPE (product, not a claim about the report): adults 19+, not pregnant or
 * breastfeeding. Anyone else gets a REASON, never a number — they keep "Enter your own"
 * and "No numbers". It is MAINTENANCE (energy balance at a steady weight), never a goal:
 * no deficit or surplus is added here, and no workout calories are added on top of the
 * activity category (which already includes activity).
 */

export const EER_METHOD = 'nasem-2023-eer-adult-v1';
export const EER_SOURCE = {
  publisher: 'National Academies of Sciences, Engineering, and Medicine',
  title: 'Dietary Reference Intakes for Energy',
  year: 2023,
  url: 'https://doi.org/10.17226/26818',
} as const;

export type EnergySex = 'male' | 'female';
export type ActivityLevel = 'inactive' | 'low_active' | 'active' | 'very_active';
export const ACTIVITY_LEVELS: readonly ActivityLevel[] = ['inactive', 'low_active', 'active', 'very_active'];

/** Table S-3 coefficients: [A, B (age), C (height cm), D (weight kg)]; EER = A − B·age + C·h + D·w. */
export const EER_COEFFICIENTS: Record<EnergySex, Record<ActivityLevel, readonly [number, number, number, number]>> = {
  male: {
    inactive: [753.07, 10.83, 6.50, 14.10],
    low_active: [581.47, 10.83, 8.30, 14.94],
    active: [1004.82, 10.83, 6.52, 15.91],
    very_active: [-517.88, 10.83, 15.61, 19.11],
  },
  female: {
    inactive: [584.90, 7.01, 5.72, 11.71],
    low_active: [575.77, 7.01, 6.60, 12.14],
    active: [710.25, 7.01, 6.54, 12.34],
    very_active: [511.83, 7.01, 9.07, 12.56],
  },
};

/** Typical error of the prediction (RMSE, kcal/d), Table S-1 notes. */
export const EER_RMSE: Record<EnergySex, number> = { male: 339, female: 246 };

/** How each category reads to a person (Table 7-1, adults; "daily living" = ~30 min walking + ~90 min light activity). */
export const ACTIVITY_META: Record<ActivityLevel, { label: string; example: string }> = {
  inactive: { label: 'Inactive', example: 'Daily living only — about 30 minutes of walking and an hour and a half of light activity like housework.' },
  low_active: { label: 'Low active', example: 'Daily living plus about 60–80 minutes of brisk walking.' },
  active: { label: 'Active', example: 'Daily living plus about 30–50 minutes of walking and around 85 minutes of vigorous activity, like cycling and tennis.' },
  very_active: { label: 'Very active', example: 'Daily living plus more than two hours of vigorous activity, like cycling, jogging and tennis.' },
};

export type EstimateUnavailable =
  | 'missing-age' | 'under-19' | 'missing-sex' | 'missing-height' | 'missing-weight' | 'stale-weight'
  | 'missing-activity' | 'pregnant-or-breastfeeding' | 'implausible-height' | 'implausible-weight' | 'implausible-age';

export interface EstimateInputs {
  ageYears: number | null;
  sex: EnergySex | null;
  heightCm: number | null;
  weightKg: number | null;
  /** The weight reading is older than Health keeps (or absent) — the caller decides. */
  weightStale?: boolean;
  activity: ActivityLevel | null;
  pregnantOrBreastfeeding?: boolean;
}

export type EstimateResult =
  | { ok: true; kcal: number; rmse: number; method: typeof EER_METHOD; inputs: Required<Pick<EstimateInputs, 'ageYears' | 'sex' | 'heightCm' | 'weightKg' | 'activity'>> }
  | { ok: false; reason: EstimateUnavailable };

/** The equation itself — no checks. Exported for the reference tests. */
export function eer(sex: EnergySex, activity: ActivityLevel, ageYears: number, heightCm: number, weightKg: number): number {
  const [a, b, c, d] = EER_COEFFICIENTS[sex][activity];
  return a - b * ageYears + c * heightCm + d * weightKg;
}

/**
 * The maintenance estimate, or the reason there is none. Every input must be a real
 * answer: nothing is defaulted, inferred or guessed. Plausibility bounds catch a typo
 * (a height of 18 cm, a weight of 800 kg) — they are not medical limits.
 */
export function estimateMaintenance(i: EstimateInputs): EstimateResult {
  if (i.ageYears === null || !Number.isFinite(i.ageYears)) return { ok: false, reason: 'missing-age' };
  if (i.ageYears < 19) return { ok: false, reason: 'under-19' };
  if (i.ageYears > 120) return { ok: false, reason: 'implausible-age' };
  if (!i.sex) return { ok: false, reason: 'missing-sex' };
  if (i.sex === 'female' && i.pregnantOrBreastfeeding) return { ok: false, reason: 'pregnant-or-breastfeeding' };
  if (i.heightCm === null || !Number.isFinite(i.heightCm)) return { ok: false, reason: 'missing-height' };
  if (i.heightCm < 120 || i.heightCm > 230) return { ok: false, reason: 'implausible-height' };
  if (i.weightKg === null || !Number.isFinite(i.weightKg)) return { ok: false, reason: i.weightStale ? 'stale-weight' : 'missing-weight' };
  if (i.weightStale) return { ok: false, reason: 'stale-weight' };
  if (i.weightKg < 30 || i.weightKg > 300) return { ok: false, reason: 'implausible-weight' };
  if (!i.activity) return { ok: false, reason: 'missing-activity' };
  const kcal = Math.round(eer(i.sex, i.activity, i.ageYears, i.heightCm, i.weightKg));
  return {
    ok: true, kcal, rmse: EER_RMSE[i.sex], method: EER_METHOD,
    inputs: { ageYears: i.ageYears, sex: i.sex, heightCm: i.heightCm, weightKg: i.weightKg, activity: i.activity },
  };
}

/** What the person reads when there is no estimate — each says what would make one possible. */
export const UNAVAILABLE_MESSAGE: Record<EstimateUnavailable, string> = {
  'missing-age': 'Add your birth date in Settings → Personal Details to see an estimate.',
  'under-19': 'This estimate is for adults 19 and over. Enter your own targets, or ask a doctor or dietitian.',
  'implausible-age': 'Check your birth date in Settings → Personal Details.',
  'missing-sex': 'Choose your gender (female or male) in Settings → Personal Details to see an estimate.',
  'pregnant-or-breastfeeding': 'This estimate doesn’t cover pregnancy or breastfeeding. Enter targets from your doctor or midwife, or track without numbers.',
  'missing-height': 'Add your height in Health → Vitals to see an estimate.',
  'implausible-height': 'Check your height in Health → Vitals.',
  'missing-weight': 'Add a weight reading in Health to see an estimate.',
  'stale-weight': 'Your last weight reading is over 90 days old — add a recent one in Health.',
  'implausible-weight': 'Check your latest weight reading in Health.',
  'missing-activity': 'Choose how active you are to see an estimate.',
};

/** cm ⇄ ft/in for Health's Height row (stored in cm). */
export function cmToFtIn(cm: number): { ft: number; inches: number } {
  const totalIn = cm / 2.54;
  let ft = Math.floor(totalIn / 12);
  let inches = Math.round(totalIn - ft * 12);
  if (inches === 12) { ft += 1; inches = 0; }
  return { ft, inches };
}
export function ftInToCm(ft: number, inches: number): number {
  return Math.round((ft * 12 + inches) * 2.54 * 10) / 10;
}

/** A Health weight reading in kg: kg as is, lb converted; any other unit is not usable. */
export function weightToKg(value: number, unit: string): number | null {
  const u = unit.trim().toLowerCase();
  if (!Number.isFinite(value) || value <= 0) return null;
  if (u === 'kg' || u === 'kgs' || u === 'kilograms') return value;
  if (u === 'lb' || u === 'lbs' || u === 'pounds') return Math.round(value * 0.45359237 * 10) / 10;
  return null;
}
